import { useTranslation } from 'react-i18next'
import { Button } from '@/components/atoms'
import { useGlobalModalStore } from '@/stores/useGlobalModalStore/useGlobalModalStore'
import { useReportsApi } from '@/hooks/useApi/useReportsApi/useReportsApi'
import useToastStore from '@/stores/useToastStore/useToastStore'
import {
    FormConfig,
    FormErrorsType,
    TextFieldConfig,
    useForm,
    useErrorTranslations,
    GetReportResponse,
} from '@/hooks'
import { useMemo, useState } from 'react'
import { Spinner } from '@/components/common/Spinner/Spinner'
import { TextField } from '@/components/common/Form/TextField/TextField'

type RenameReportContentProps = {
    report: GetReportResponse
    handleOnSuccess?: () => void
}

export const RenameReportContent = ({
    report,
    handleOnSuccess,
}: RenameReportContentProps) => {
    const { t } = useTranslation('translations')
    const [errors, setErrors] = useState<FormErrorsType>({})

    const formConfig: FormConfig = useMemo(
        () => ({
            reportName: {
                type: 'text' as TextFieldConfig['type'],
                label: t('reporting.rename_report.input_label'),
                placeholder: t('reporting.rename_report.input_placeholder'),
                value: report.name,
                required: true,
            },
        }),
        [t, report]
    )

    const {
        getInputProps,
        setFieldValue,
        formState,
        isValid,
        setAllFieldsDirty,
    } = useForm(formConfig)

    const textFieldProps = getInputProps('reportName')
    const handleChange = (value: string) => {
        setFieldValue('reportName', value)
        setErrors({})
    }

    const errorString = useErrorTranslations()
    const mergedErrors = useMemo(() => {
        if (!textFieldProps.errors) {
            return errors
        }

        return {
            ...errors,
            ...textFieldProps.errors,
        }
    }, [errors, textFieldProps.errors])

    const addAlert = useToastStore((state) => state.addAlert)
    const setModalState = useGlobalModalStore((state) => state.setModalState)
    const { useUpdateReportMutation } = useReportsApi()
    const {
        mutate: renameReportMutation,
        isPending,
        reset,
    } = useUpdateReportMutation()

    const closeModal = () => {
        setModalState({ isOpen: false })
    }

    const handleRename = (newReportName: string) => {
        if (!isValid) {
            setAllFieldsDirty()
            return
        }
        renameReportMutation(
            {
                customReportId: report.customReportId,
                data: {
                    name: newReportName,
                    fields: report.fields,
                    type: report.type,
                    filters: report.filters,
                },
            },
            {
                onSuccess: () => {
                    closeModal()
                    handleOnSuccess?.()

                    addAlert({
                        variant: 'success',
                        title: t('reporting.rename_report.success_toast.title'),
                        description: t(
                            'reporting.rename_report.success_toast.description',
                            {
                                reportName: newReportName,
                            }
                        ),
                    })
                },
                onError: (error) => {
                    const data = error.response?.data

                    if (
                        typeof data === 'object' &&
                        data !== null &&
                        'message' in data &&
                        typeof data.message === 'string' &&
                        data.message === 'Conflict'
                    ) {
                        setErrors({
                            ...errors,
                            reportName:
                                errorString['ERR_DUPLICATE_REPORT_NAME'],
                        })
                        reset()
                        return
                    }
                    closeModal()
                    addAlert({
                        variant: 'error',
                        title: t('reporting.rename_report.error_toast.title'),
                        description: t(
                            'reporting.rename_report.error_toast.description'
                        ),
                    })
                    reset()
                },
            }
        )
    }
    const renameSaveLabel = isPending
        ? t('reporting.rename_report.loading_save_button')
        : t('reporting.rename_report.save_button')

    return (
        <div className="text-sm">
            <div className="space-y-6 px-6 pb-6 pt-2">
                <TextField
                    {...getInputProps('reportName')}
                    onChange={handleChange}
                    disabled={isPending}
                    errors={mergedErrors}
                />
            </div>
            <div className="flex flex-row-reverse gap-4 border-t border-t-neutral-200 px-6 py-4">
                <Button
                    name="rename"
                    size="large"
                    onClick={() => {
                        handleRename(formState['reportName'].toString())
                    }}
                    disabled={isPending}
                    className="whitespace-nowrap"
                    data-testid="rename-confirm-button"
                >
                    {renameSaveLabel}
                    {isPending && (
                        <span data-testid="loading-state">
                            <Spinner size={16} width={3} />
                        </span>
                    )}
                </Button>
                <Button
                    name="cancel"
                    variant={'outlinePrimary'}
                    size="large"
                    onClick={closeModal}
                    className="whitespace-nowrap"
                    data-testid="cancel-button"
                >
                    {t('reporting.rename_report.cancel_button')}
                </Button>
            </div>
        </div>
    )
}
